import type { FeasibleVenue, PracticePairing } from '@region13/shared-types'
import {
  UNLIT_AUTUMN_ALERT, droppedByLitFilter, litFilterNotice, rankableVenues, sharedSubfieldNote, weekdayList,
} from '../lib/practicePrefsCopy'

const rankButtonClass =
  'min-h-[32px] min-w-[32px] rounded-md border border-line text-xs font-semibold text-ink-soft ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon/40 disabled:opacity-30'

/** Moves the item at index `i` by `dir` within `arr`, no-op past either end. */
function moveItem<T>(arr: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir
  if (j < 0 || j >= arr.length) return arr
  const next = [...arr]
  const a = next[i]
  const b = next[j]
  if (a === undefined || b === undefined) return arr
  next[i] = b
  next[j] = a
  return next
}

/** A venue's title, its real permit days, and (when it has no lights) the
 *  autumn transition alert. One component renders the pairing so the ranked
 *  and still-to-rank lists cannot drift apart. */
function VenueLabel({ venue }: { venue: Pick<FeasibleVenue, 'title' | 'weekdays' | 'lit'> }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="block truncate text-ink">{venue.title}</span>
      <span className="block text-xs text-ink-soft">{weekdayList(venue.weekdays)}</span>
      {!venue.lit && <span className="mt-1 block text-xs text-brand-maroon">{UNLIT_AUTUMN_ALERT}</span>}
    </span>
  )
}

/** One already-ranked venue's row: its rank, label, and reorder/remove
 *  controls. Split out so the picker's own render stays focused on the two
 *  list shapes (ranked, and still to rank). */
function RankedVenueRow({
  venue, index, isLast, onMove, onRemove,
}: {
  venue: FeasibleVenue | undefined
  index: number
  isLast: boolean
  onMove: (dir: -1 | 1) => void
  onRemove: () => void
}) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className="w-5 pt-0.5 text-ink-soft">{index + 1}.</span>
      {venue ? (
        <VenueLabel venue={venue} />
      ) : (
        <span className="min-w-0 flex-1 text-ink-soft">(unknown venue)</span>
      )}
      <button type="button" className={rankButtonClass} disabled={index === 0} onClick={() => onMove(-1)}>
        Up
      </button>
      <button type="button" className={rankButtonClass} disabled={isLast} onClick={() => onMove(1)}>
        Down
      </button>
      <button type="button" className={rankButtonClass} onClick={onRemove}>
        X
      </button>
    </li>
  )
}

/** Ranked venue picker. The server has already narrowed `venues` to what this
 *  team can actually be given (age-eligible, with a long-enough permit
 *  window); `rankableVenues` narrows it once more for a back-to-back coach
 *  (spec D6). A venue that narrowing removed is NOT rendered as a ranked row
 *  even if the coach had ranked it, because the alert above is what tells them
 *  it went. */
export function VenuePreferencePicker({
  venues, value, onChange, pairing, teamAge,
}: {
  venues: FeasibleVenue[]
  value: string[]
  onChange: (v: string[]) => void
  pairing: PracticePairing | null
  teamAge: number | null
}) {
  const offered = rankableVenues(venues, pairing)
  const ranked = value.filter((slug) => offered.some((v) => v.slug === slug))
  const available = offered.filter((v) => !ranked.includes(v.slug))
  const notice = litFilterNotice(droppedByLitFilter(venues, value, pairing))
  const sharing = sharedSubfieldNote(teamAge)

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-ink-soft">Preferred venues (in order)</span>
      {notice && <p role="alert" className="text-xs text-brand-maroon">{notice}</p>}
      {sharing && <p className="text-xs text-ink-soft">{sharing}</p>}
      {ranked.length === 0 ? (
        <p className="text-sm text-ink-soft">No venues ranked yet. Add one below.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {ranked.map((slug, i) => (
            <RankedVenueRow
              key={slug}
              venue={offered.find((v) => v.slug === slug)}
              index={i}
              isLast={i === ranked.length - 1}
              onMove={(dir) => onChange(moveItem(ranked, i, dir))}
              onRemove={() => onChange(ranked.filter((s) => s !== slug))}
            />
          ))}
        </ol>
      )}
      {available.length > 0 && (
        <ul className="flex flex-col gap-1">
          {available.map((v) => (
            <li key={v.slug}>
              <button
                type="button"
                onClick={() => onChange([...ranked, v.slug])}
                className="flex w-full items-start gap-0.5 rounded-lg border border-line bg-surface px-3 py-2 text-left text-sm hover:border-brand-red-dark/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon/40"
              >
                <VenueLabel venue={v} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
